import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { authAPI, setSessionToken } from './api';
import { toast } from 'sonner';
import { getAuthRedirect, clearAuthRedirect } from './utils/mobileStorage';
import { LanguageProvider } from './i18n';

// Pages
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Analyzer from './pages/Analyzer';
import Results from './pages/Results';
import PropertyConfirmation from './pages/PropertyConfirmation';
import SavedDeals from './pages/SavedDeals';
import CompareDeals from './pages/CompareDeals';
import Inbox from './pages/Inbox';
import PublicSubmit from './pages/PublicSubmit';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import SharedAnalysis from './pages/SharedAnalysis';
import CriteriaPage from './pages/CriteriaPage';
import SiteFooter from './components/SiteFooter';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Contact from './pages/Contact';
import Pricing from './pages/Pricing';
import AdminDashboard from './pages/AdminDashboard';
import ProviderBenchmark from './pages/ProviderBenchmark';

// Context for user state
export const UserContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback establishes the session first. Emergent returns #session_id=,
    // direct Google returns #token= (or #error= on failure).
    const h = window.location.hash || '';
    if (h.includes('session_id=') || h.includes('token=') || h.includes('error=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading CHEKSTE...</p>
        </div>
      </div>
    );
  }

  return (
    <UserContext.Provider value={{ user, setUser, refreshUser: checkAuth }}>
      {/* Language is context state so switching it re-renders instead of reloading the
          page. The reload used to wipe every unsaved edit — most visibly the user's own
          ARV, which silently reverted to our estimate. */}
      <LanguageProvider>
        <Router>
          <AppRouter />
        </Router>
      </LanguageProvider>
    </UserContext.Provider>
  );
}

// AppRouter - detects session_id in hash BEFORE routes render to prevent race conditions
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment (hash) for an auth callback - runs synchronously during render
  // CRITICAL: Must check hash, not query params. Emergent returns #session_id=xxx;
  // direct Google returns #token=xxx (or #error= on failure).
  const h = location.hash || '';
  if (h.includes('session_id=') || h.includes('token=') || h.includes('error=')) {
    return <AuthCallback />;
  }
  
  return (
    <div className="min-h-screen bg-background">
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/submit/:username" element={<PublicSubmit />} />
        <Route path="/shared/:analysisId" element={<SharedAnalysis />} />
        
        {/* Auth callback - also handle /auth/callback route */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Checkout success/cancel */}
        <Route path="/billing/success" element={<CheckoutSuccess />} />
        <Route path="/billing/cancel" element={<CheckoutCancel />} />
        
        {/* Semi-public routes (work with or without auth) */}
        <Route path="/analyze" element={<Analyzer />} />
        <Route path="/confirm" element={<PropertyConfirmation />} />
        <Route path="/results" element={<Results />} />
        <Route path="/results/:analysisId" element={<Results />} />
        
        {/* App routes */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/saved" element={<SavedDeals />} />
        <Route path="/compare" element={<CompareDeals />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/criteria" element={<CriteriaPage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/billing" element={<Billing />} />
        
        {/* Legal Pages */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/pricing" element={<Pricing />} />
        
        {/* Admin Dashboard */}
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/provider-benchmark" element={<ProviderBenchmark />} />
        
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      {/* Terms · Privacy · Contact under every page. The landing page carries its own
          footer (same links, with the logo lockup). */}
      {location.pathname !== '/' && <SiteFooter />}
      <Toaster />
    </div>
  );
}

// Auth callback handler
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = React.useContext(UserContext);
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent duplicate processing (StrictMode protection)
      if (hasProcessed.current) return;
      hasProcessed.current = true;
      
      console.log('[AuthCallback] Starting auth callback processing...');
      console.log('[AuthCallback] Current URL:', window.location.href);
      console.log('[AuthCallback] Hash:', window.location.hash);
      
      // Read the callback from the URL FRAGMENT (hash), not query params.
      // Direct Google: #token=xxx&next=/path (or #error=xxx). Emergent: #session_id=xxx
      const hash = window.location.hash;

      const errorMatch = hash.match(/error=([^&]+)/);
      if (errorMatch) {
        console.error('[AuthCallback] OAuth error:', errorMatch[1]);
        setError('Authentication failed. Please try again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      // --- Direct Google path: backend already minted our token ---
      const tokenMatch = hash.match(/token=([^&]+)/);
      if (tokenMatch) {
        const token = tokenMatch[1];
        const nextMatch = hash.match(/next=([^&]+)/);
        try {
          setSessionToken(token);  // store for Authorization: Bearer + Safari
          const me = await authAPI.getMe();
          setUser(me.data);
          toast.success('Welcome to CHEKSTE!');
          const redirectUrl = (nextMatch ? decodeURIComponent(nextMatch[1]) : null) || getAuthRedirect();
          window.history.replaceState(null, '', window.location.pathname);
          navigate(redirectUrl || '/analyze', { replace: true });
        } catch (err) {
          console.error('[AuthCallback] Google token verify failed:', err);
          clearAuthRedirect();
          setError('Authentication failed. Please try again.');
          setTimeout(() => navigate('/'), 2000);
        }
        return;
      }

      // --- Legacy Emergent path: exchange session_id via backend ---
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      const sessionId = sessionIdMatch ? sessionIdMatch[1] : null;

      console.log('[AuthCallback] Extracted session_id:', sessionId ? `${sessionId.substring(0, 8)}...` : 'null');

      if (!sessionId) {
        console.error('[AuthCallback] No session ID in hash');
        setError('No session ID provided');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      try {
        console.log('[AuthCallback] Calling createSession API...');
        // Exchange session_id for user data via backend
        const response = await authAPI.createSession({ session_id: sessionId });
        
        console.log('[AuthCallback] createSession response:', { 
          success: response.data.success, 
          hasUser: !!response.data.user,
          hasToken: !!response.data.session_token 
        });
        
        if (response.data.success && response.data.user) {
          setUser(response.data.user);
          toast.success('Welcome to CHEKSTE!');
          
          // Get stored redirect URL using mobile-safe storage
          const redirectUrl = getAuthRedirect();
          console.log('[AuthCallback] Redirect URL from storage:', redirectUrl);
          
          // Clear the hash from URL
          window.history.replaceState(null, '', window.location.pathname);
          
          // Navigate to stored redirect or default to /analyze
          const finalRedirect = redirectUrl || '/analyze';
          console.log('[AuthCallback] Navigating to:', finalRedirect);
          navigate(finalRedirect, { replace: true });
        } else {
          console.error('[AuthCallback] Login failed - no user in response');
          throw new Error('Login failed');
        }
      } catch (err) {
        console.error('[AuthCallback] Auth callback error:', err);
        console.error('[AuthCallback] Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        clearAuthRedirect(); // Clean up on error
        setError('Authentication failed. Please try again.');
        setTimeout(() => navigate('/'), 2000);
      }
    };

    handleCallback();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-red-600 mb-2">{error}</p>
          <p className="text-muted-foreground text-sm">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// Checkout success page
const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const { refreshUser } = React.useContext(UserContext);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const checkStatus = async () => {
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        // Refresh user data to get updated plan
        await refreshUser();
      }
      setTimeout(() => navigate('/billing'), 2000);
    };
    checkStatus();
  }, [searchParams, refreshUser, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-primary mb-3">Payment Successful!</h1>
        <p className="text-muted-foreground mb-6">
          Thank you for upgrading! Your new features are now active.
        </p>
        <p className="text-sm text-muted-foreground">Redirecting to billing...</p>
      </div>
    </div>
  );
};

// Checkout cancel page
const CheckoutCancel = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => navigate('/billing'), 2000);
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-primary mb-3">Checkout Cancelled</h1>
        <p className="text-muted-foreground mb-6">
          Your payment was cancelled. No charges were made.
        </p>
        <p className="text-sm text-muted-foreground">Redirecting to billing...</p>
      </div>
    </div>
  );
};

export default App;
